import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'src/store/store';
import { getElementHeight } from 'src/utils/elementSize';
import { pluralize } from 'src/utils/string';
import { Element, Section } from 'src/constants/footfall';
import i18n from 'src/utils/translate';
import Roles from 'src/components/roles';
import RelativeTime from 'src/components/relativeTime';
import UserLink from 'src/modules/shared/links/userLink';
import Icon from 'src/components/icon';
import { Size } from 'src/components/avatar';
import PostLink from 'src/modules/shared/links/postLink';
import Separator from 'src/components/separator';
import { textLight, titleSmall } from 'styles-js/mixins/typography';
export default function PostHeaderInfo() {
  const post = useSelector(({
    pages
  }) => pages.singlePost.post);
  const isCommunityMemberOrHUStaff = useSelector(({
    context
  }) => context.currentUser?.isCommunityMemberOrHUStaff);
  const communitySlug = useSelector(({
    context
  }) => context.currentCommunity?.slug);
  const [showHeaderAuthorInfo, setShowHeaderAuthorInfo] = useState(true);
  useEffect(() => {
    if (!post.imageUrl) setShowHeaderAuthorInfo(setHeaderAuthorInfo());
  }, [post]);
  if (!communitySlug || !post) return null;
  if (post.isPrivate && !isCommunityMemberOrHUStaff) {
    return <InfoSection>
        <RelativeTime rawTime={post.dateCreated} />
        <Separator />
        <PostLink anchorToResponses={true} communitySlug={communitySlug} isPrivate={post.isPrivate} postId={post.id} postTypeId={post.postTypeId} urlEncodedTitle={post.urlEncodedTitle}>
          {pluralize(i18n.t('%1$s Reply'), i18n.t('%1$s Replies'), post.totalResponses ?? 0)}
        </PostLink>
      </InfoSection>;
  }
  return <>
      {showHeaderAuthorInfo && <StyledUserLink avatarSize={Size.Small} signupContext={{
      element: Element.Avatar,
      section: Section.Post
    }} user={post.author} />}
      <Info data-sentry-element="Info" data-sentry-source-file="postHeaderInfo.tsx">
        {showHeaderAuthorInfo && <InfoSection $showMargin={showHeaderAuthorInfo} data-testid="info-section">
            <UserLink className="author" signupContext={{
          element: Element.UserLink,
          section: Section.Post
        }} user={post.author} />
            {post?.author?.roles?.length > 0 && <Roles roles={post.author.roles} />}
            <Separator className="hidden-sm" />
          </InfoSection>}
        <InfoSection $showMargin={showHeaderAuthorInfo} data-sentry-element="InfoSection" data-sentry-source-file="postHeaderInfo.tsx">
          <RelativeTime rawTime={post.dateCreated} data-sentry-element="RelativeTime" data-sentry-source-file="postHeaderInfo.tsx" />
          <Separator data-sentry-element="Separator" data-sentry-source-file="postHeaderInfo.tsx" />
          <PostLink anchorToResponses={true} communitySlug={communitySlug} isPrivate={post.isPrivate} postId={post.id} postTypeId={post.postTypeId} urlEncodedTitle={post.urlEncodedTitle} data-sentry-element="PostLink" data-sentry-source-file="postHeaderInfo.tsx">
            {pluralize(i18n.t('%1$s Reply'), i18n.t('%1$s Replies'), post.totalResponses ?? 0)}
          </PostLink>
          {post.isPrivate && <Icon icon="lock" />}
        </InfoSection>
      </Info>
    </>;
}
const setHeaderAuthorInfo = () => {
  if (typeof document !== 'undefined') {
    const classNames = ['.js-community-header', '.js-verification-bar', '.js-sitebar', '.js-post-header', '.js-post-body', '.written-by'];
    const VIEWPORT_HEIGHT = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    // set to auto, but this is max allowed based on max height of its inside elements + margin*2
    const cardHeight = 179 + 14 * 2;
    const elementsHeight = classNames.reduce((acc, i) => {
      return acc + getElementHeight(i);
    }, cardHeight);
    if (VIEWPORT_HEIGHT > elementsHeight) return false;
  }
  return true;
};
const Info = styled.div`
  display: inline-flex;
  vertical-align: middle;
  @media (${({
  theme
}) => theme.underScreenMedium}) {
    flex-direction: column;
  }
`;
const InfoSection = styled.div<{
  $showMargin?: boolean;
}>`
  ${textLight}
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  margin-left: ${props => props.$showMargin ? '12px' : '0'};

  .author {
    ${titleSmall}
  }
  a {
    ${textLight}
  }
`;
const StyledUserLink = styled(UserLink)`
  vertical-align: middle;
  display: inline-block;
`;